import React, { useContext, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';

import { PATH_ROUTES } from '../utils/pathRoutes';

import LogoImage from '../images/logo.png';
import ParadigmaLogoImage from '../images/paradigma_logo.png';
import HeroLogoImage from '../images/hero_logo_white.png';
import { isLuonaHost } from '../utils/luonaSpecificData';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
import { isAiSessionsEnabled } from '../utils/aiSessions';
import { canAccessTranslations } from '../utils/translationAccess';
import i18n from '../i18n';
import { manifest } from '../Manifest';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';

import LeftSidebarMenuItem from '../components/LeftSidebarMenuItem';
import Divider from '../components/Divider';
import UserProfile from '../components/UserProfile';
import extranetPagesConfigurator from '../utils/extranetPagesConfigurator';

import { useLocaleStorage } from '../utils/localStorage';

import {
  getEulaUrl,
} from '../services/eulaService';
import AccountDataConfirmation from './AccountDataConfirmation';
import translationsSidebarIcon from '../images/translationsSidebarIcon.svg';

const Sidebar = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  color: #ffffff;
`;

const Logo = styled.img`
  width: ${({fullSize}) => fullSize ? '100%' : '70px'};
`;

const LogoContainer = styled.div`
  width: 100%;
  padding: 32px;
  box-sizing: border-box;
`;

const BottomButtons = styled.div`
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
`;

const StyledFullAvailablrHeight = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const StyledVersionLabel = styled.span`
  padding: 0 32px;
  font-size: 10px;
`;

const getMenuItemsDependOnRole = (user, invoiceCount, translationsCount = 0) => {
  const showTranslations = canAccessTranslations(user);
  const showAiSessions = isAiSessionsEnabled(user);
  const additionalPages = [];
  if (user?.role === 'admin') {
    additionalPages.push({
      to: '/interpreters',
      text: i18n('calendars'),
      icon: 'event',
      font: 'material'
    });
  }

  if (user?.aiLanguagesOnly) {
    return [
      {
        to: '/',
        text: i18n('od_type_ai_subtitle'),
        icon: 'smart_toy',
        font: 'material'
      },
      ...(showAiSessions ? [{
        to: '/ai-interpreter',
        text: i18n('ai_interpreter'),
        icon: 'record_voice_over',
        font: 'material'
      }] : []),
      ...additionalPages,
      {
        to: '/settings',
        text: i18n('settings'),
        icon: 'settings',
        font: 'material'
      }
    ];
  }

  if (user?.type === 'Interpreter') {
    const showInterpreterTranslations = isHeroBrandEnabled();
    return [{
      to: '/',
      text: i18n('my_bookings'),
      icon: 'event',
      font: 'material'
    }, {
      to: '/reports',
      text: i18n('reports'),
      icon: 'assignment',
      font: 'material'
    },
    ...(showInterpreterTranslations ? [{
      to: '/translations',
      text: i18n('translations'),
      customIcon: <img src={translationsSidebarIcon} alt="" width={24} height={24} />,
      count: translationsCount,
    }] : []),
    ...additionalPages,
    {
      to: '/settings',
      text: i18n('settings'),
      icon: 'settings',
      font: 'material'
    }];
  }
  const pages = extranetPagesConfigurator(user);
  if (pages && pages.length > 0) {
    return [{
      to: '/',
      text: i18n('reports'),
      icon: 'assignment',
      font: 'material'
    },
    ...(showAiSessions ? [{
      to: '/ai-interpreter',
      text: i18n('ai_interpreter'),
      icon: 'record_voice_over',
      font: 'material'
    }] : []),
    {
      to: '/booking',
      text: i18n('my_bookings'),
      icon: 'event',
      font: 'material'
    }, {
      to: '/billing',
      text: i18n('billing'),
      icon: 'grading',
      font: 'material',
      count: invoiceCount,
      dividerAfter: isHeroBrandEnabled()
    },
    ...(showTranslations ? [{
      to: '/translations',
      text: i18n('translations'),
      customIcon: <img src={translationsSidebarIcon} alt="" width={24} height={24} />,
      count: translationsCount,
    }] : []),
    ...additionalPages,
    {
      to: '/settings',
      text: i18n('settings'),
      icon: 'settings',
      font: 'material'
    }];
  }
  return [{
    to: '/',
    text: i18n('my_bookings'),
    icon: 'event',
    font: 'material'
  },
  ...(showAiSessions ? [{
    to: '/ai-interpreter',
    text: i18n('ai_interpreter'),
    icon: 'record_voice_over',
    font: 'material'
  }] : []),
  {
    to: '/billing',
    text: i18n('billing'),
    icon: 'grading',
    font: 'material',
    count: invoiceCount,
    dividerAfter: isHeroBrandEnabled()
  },
  ...(showTranslations ? [{
    to: '/translations',
    text: i18n('translations'),
    customIcon: <img src={translationsSidebarIcon} alt="" width={24} height={24} />,
    dividerAfter: isHeroBrandEnabled(),
    count: translationsCount,
  }] : []),
  {
    to: '/reports',
    text: i18n('reports'),
    icon: 'assignment',
    font: 'material'
  }, {
    to: '/settings',
    text: i18n('settings'),
    icon: 'settings',
    font: 'material'
  }];
};

const LeftSidebar = () => {
  const appContext = useContext(AppContext);
  const { colors, layout } = useContext(ThemeContext);
  const isLuona = () => {
    return !!localStorage.getItem('luonaId');
  };

  const [user, setUser] = useState(appContext.user);
  const [currentPath, setCurrentPath] = useState(appContext.history.location.pathname);
  const [appVersion] = useState(manifest.appVersion);
  const [isOnMeeting] = useState(currentPath.includes(PATH_ROUTES.aiMeetings) || false);
  
  const [timeToShowConfirmation, setTimeToShowConfirmation] = useLocaleStorage('timeToShowConfirmation', null);
  const timeToNextShowConfirmation = 24 * 60 * 60 * 1000;
  const isTimeToShowConfirmation = useMemo(() => {
    let timeDiff = timeToShowConfirmation ? Date.now() - timeToShowConfirmation : 0;
    return timeToShowConfirmation === null || timeDiff >= timeToNextShowConfirmation;
  }, [timeToShowConfirmation, timeToNextShowConfirmation]); 

  const [accountInformationConfirmation, setAccountInformationConfirmationIsOpen] = React.useState(
    !user?.profileVerifiedAt &&
    isTimeToShowConfirmation &&
    !isLuona()
  );
  // eslint-disable-next-line no-unused-vars
  const [currentUserGroup, setCurrentUserGroup] = React.useState(user.userGroup ? {
    id: user.userGroup.id,
    name: user.userGroup.name
  } : {});

  const [menuItems, setMenuItems] = useState(getMenuItemsDependOnRole(appContext.user, appContext.invoiceCount, appContext.translationsCount));

  useEffect(() => {
    let updated = false;
    if (!updated) {
      setMenuItems(getMenuItemsDependOnRole(appContext.user, appContext.invoiceCount, appContext.translationsCount));
    }
    return () => {
      updated = true;
    };
  }, [appContext.user, appContext.invoiceCount, appContext.translationsCount]);

  useEffect(() => {
    let updated = false;
    if (!updated) {
      setCurrentPath(appContext.history.location.pathname);
    }
    return () => {
      updated = true;
    };
  }, [appContext.history.location.pathname]);

  useEffect(() => {
    let updated = false;
    if (!updated) {
      setUser(appContext.user);
      setCurrentUserGroup(appContext.user.userGroup ? {
        id: appContext.user.userGroup.id,
        name: appContext.user.userGroup.name
      } : {});
    }
    return () => {
      updated = true;
    };
  }, [appContext.user]);

  const isParadigma = isLuonaHost();
  const isHero = isHeroBrandEnabled();
  const logo = isHero ? HeroLogoImage : (isParadigma ? ParadigmaLogoImage : LogoImage);
  const altText = isHero ? 'Hero' : (isParadigma ? 'Paradigmasoft' : 'Tulka');

  return useMemo(() => {
    return (
      <Sidebar colors={colors}>
        <Modal
          isOpen={accountInformationConfirmation}
          style={layout.modalConfirmationStyles}
          onRequestClose={() => { 
            setAccountInformationConfirmationIsOpen(false);
            setTimeToShowConfirmation(Date.now());
          }}
        >
          <AccountDataConfirmation
            closeModal={() => {
              setAccountInformationConfirmationIsOpen(false);
              setTimeToShowConfirmation(null);
              if (currentPath === '/settings') {
                appContext.history.go(0);
              }
            }}
          />
        </Modal>    
        <LogoContainer>
          <Logo src={logo} alt={altText} fullSize={isParadigma || isHero}/>
        </LogoContainer>
        {!isOnMeeting && user ? (
          <UserProfile/>
        ) : null}
        <StyledFullAvailablrHeight>
          {!isOnMeeting && menuItems.map((props) => {
            const { dividerAfter, ...menuProps } = props;
            return (
              <React.Fragment key={props.to}>
                <LeftSidebarMenuItem currentPath={currentPath} dataTest={props.text + 'Nav'} {...menuProps} />
                {dividerAfter && <Divider color="rgba(255, 255, 255, 0.3)" margin="16px 24px" width="auto" />}
              </React.Fragment>
            );
          })}
        </StyledFullAvailablrHeight>
        <BottomButtons>
          <LeftSidebarMenuItem
            key="terms"
            href={getEulaUrl(user)}
            target="_blank"
            rel="noopener noreferrer"
            text={i18n('termsOfUse')}
            icon="description"
            font="material"
            dataTest={'termsURLNav'}
          />
          <LeftSidebarMenuItem 
            key="logout"
            to="/logout"
            text={i18n('logout')}
            icon="logout"
            font="material"
            dataTest={'logoutNav'}
          />
        </BottomButtons>
        <StyledVersionLabel>{appVersion}</StyledVersionLabel>
      </Sidebar>
    );
  }, [
    accountInformationConfirmation,
    appContext.history,
    appVersion,
    colors,
    layout,
    currentPath,
    setTimeToShowConfirmation,
    menuItems,
    user,
    isOnMeeting,
    altText,
    isParadigma,
    isHero,
    logo
  ]);
};

export default LeftSidebar;
